/**
 * Audit log module categories.
 * Must match the `module` field values used by the API.
 */
export enum EAuditModule {
  USER = 'user',
  SPACE = 'space',
  REPORT = 'report',
  FEEDBACK = 'feedback',
  ADMIN = 'admin',
  SETTINGS = 'settings',
  X_POSTS = 'x_posts',
}

/** Array of all module values (for dropdown filters) */
export const AUDIT_MODULES = Object.values(EAuditModule);

/**
 * All audit log action types used by backoffice and admin services.
 *
 * These values must match the `action` strings passed to
 * `AuditLogService.log()` on the API side.
 *
 * Grouped by domain for readability.
 */
export enum EAuditAction {
  // User management (admin.service)
  CREATE_USER = 'CREATE_USER',
  UPDATE_USER = 'UPDATE_USER',
  BAN_USER = 'BAN_USER',
  UNBAN_USER = 'UNBAN_USER',
  MUTE_USER = 'MUTE_USER',
  UNMUTE_USER = 'UNMUTE_USER',
  DELETE_USER = 'DELETE_USER',

  // Space owner management (backoffice.service)
  CREATE_SPACE_OWNER = 'CREATE_SPACE_OWNER',
  UPDATE_SPACE_OWNER = 'UPDATE_SPACE_OWNER',
  ENABLE_SPACE_OWNER = 'ENABLE_SPACE_OWNER',
  DISABLE_SPACE_OWNER = 'DISABLE_SPACE_OWNER',

  // Space management (backoffice.service + admin.service)
  CREATE_SPACE = 'CREATE_SPACE',
  UPDATE_SPACE = 'UPDATE_SPACE',
  UPDATE_SPACE_VERIFICATION = 'UPDATE_SPACE_VERIFICATION',
  REASSIGN_SPACE_OWNER = 'REASSIGN_SPACE_OWNER',
  DEACTIVATE_SPACE = 'DEACTIVATE_SPACE',
  ACTIVATE_SPACE = 'ACTIVATE_SPACE',
  DELETE_SPACE = 'DELETE_SPACE',

  // Space members (backoffice.service)
  ADD_SPACE_MEMBERS = 'ADD_SPACE_MEMBERS',
  REMOVE_SPACE_MEMBER = 'REMOVE_SPACE_MEMBER',
  SET_SPACE_ADMINS = 'SET_SPACE_ADMINS',

  // Room management (backoffice.service)
  CREATE_ROOM = 'CREATE_ROOM',
  UPDATE_ROOM = 'UPDATE_ROOM',
  DELETE_ROOM = 'DELETE_ROOM',
  CONVERT_ROOM_TO_PRIVATE = 'CONVERT_ROOM_TO_PRIVATE',
  CONVERT_ROOM_TO_PUBLIC = 'CONVERT_ROOM_TO_PUBLIC',
  ADD_ROOM_MEMBERS = 'ADD_ROOM_MEMBERS',
  REMOVE_ROOM_MEMBER = 'REMOVE_ROOM_MEMBER',
  ASSIGN_ROOM_ADMINS = 'ASSIGN_ROOM_ADMINS',
  SET_ROOM_MUTE = 'SET_ROOM_MUTE',

  // Messages (backoffice.service)
  DELETE_MESSAGE = 'DELETE_MESSAGE',

  // Invitations (backoffice.service)
  GENERATE_INVITATION_CODES = 'GENERATE_INVITATION_CODES',
  GENERATE_SPACE_INVITATION_LINK = 'GENERATE_SPACE_INVITATION_LINK',
  UPDATE_INVITATION_BASE_URL = 'UPDATE_INVITATION_BASE_URL',

  // Sensitive words (backoffice.service)
  ADD_SENSITIVE_WORD = 'ADD_SENSITIVE_WORD',
  BATCH_UPLOAD_SENSITIVE_WORDS = 'BATCH_UPLOAD_SENSITIVE_WORDS',
  REMOVE_SENSITIVE_WORD = 'REMOVE_SENSITIVE_WORD',
  REPLACE_PLATFORM_SENSITIVE_WORDS = 'REPLACE_PLATFORM_SENSITIVE_WORDS',

  // Content & notifications (backoffice.service)
  UPDATE_CONTENT = 'UPDATE_CONTENT',
  UPDATE_PLATFORM_CONFIG = 'UPDATE_PLATFORM_CONFIG',
  SEND_NOTIFICATION = 'SEND_NOTIFICATION',

  // X Posts (x-posts.controller) — the 内容管理 module (SITOSP-383)
  CREATE_X_POST = 'CREATE_X_POST',
  UPDATE_X_POST = 'UPDATE_X_POST',
  DELETE_X_POST = 'DELETE_X_POST',
  PUBLISH_X_POST = 'PUBLISH_X_POST',
  UNPUBLISH_X_POST = 'UNPUBLISH_X_POST',
  TRIGGER_CRAWL = 'TRIGGER_CRAWL',

  // Reports & feedback (backoffice.service)
  WARN_REPORTED_USER = 'WARN_REPORTED_USER',
  UPDATE_FEEDBACK_STATUS = 'UPDATE_FEEDBACK_STATUS',
  ADD_FEEDBACK_REPLY = 'ADD_FEEDBACK_REPLY',

  // Backoffice admin (backoffice.service)
  BACKOFFICE_ADMIN_CREATED = 'BACKOFFICE_ADMIN_CREATED',
  BACKOFFICE_ADMIN_UPDATED = 'BACKOFFICE_ADMIN_UPDATED',
  BACKOFFICE_OWNERSHIP_TRANSFERRED = 'BACKOFFICE_OWNERSHIP_TRANSFERRED',

  // Categories (admin.service)
  CREATE_CATEGORY = 'CREATE_CATEGORY',
  UPDATE_CATEGORY = 'UPDATE_CATEGORY',
  DELETE_CATEGORY = 'DELETE_CATEGORY',
}

/** Backward-compatible array of all audit action types (for dropdown filters, etc.) */
export const AUDIT_ACTION_TYPES = Object.values(EAuditAction);

/** Backward-compatible type alias */
export type AuditActionType = EAuditAction;
